import ReferenceService from '@/services/reference';

// Reference store module
//
// Cross-page lookup data that isn't medication-domain-specific: facility names
// and patient populations. (The medication list and categories live in the
// `medication` module.)
//
// Target-application module convention: no `namespaced`, object `state`,
// SCREAMING_SNAKE mutations paired with a `*_LOADED` flag, `setX` actions
// returning a Promise, and `x` / `xLoaded` getters.

export default {
  state: {
    facilityNames: [],
    facilityNamesLoaded: false,
    patientPopulations: [],
    patientPopulationsLoaded: false,
  },

  mutations: {
    SET_REFERENCE_FACILITY_NAMES(state, facilityNames) {
      state.facilityNames = facilityNames;
    },
    SET_REFERENCE_FACILITY_NAMES_LOADED(state, loaded) {
      state.facilityNamesLoaded = loaded;
    },
    SET_REFERENCE_PATIENT_POPULATIONS(state, patientPopulations) {
      state.patientPopulations = patientPopulations;
    },
    SET_REFERENCE_PATIENT_POPULATIONS_LOADED(state, loaded) {
      state.patientPopulationsLoaded = loaded;
    },
  },

  actions: {
    // Note: deliberately does NOT clear the existing list up front. Blanking it
    // before the request resolves would empty the filter dropdown on every
    // re-dispatch; the commit below replaces it atomically once data arrives.
    setReferenceFacilityNames({ commit }) {
      commit('SET_REFERENCE_FACILITY_NAMES_LOADED', false);
      return new Promise((resolve, reject) => {
        ReferenceService.getFacilityNames()
          .then((response) => {
            // Contract #1: { facilities: string[] }. Tolerates a bare array so
            // the endpoint can return either without a store change.
            const facilityNames = Array.isArray(response) ? response : response.facilities || [];
            commit('SET_REFERENCE_FACILITY_NAMES', facilityNames);
            resolve();
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            commit('SET_REFERENCE_FACILITY_NAMES_LOADED', true);
          });
      });
    },
    setReferencePatientPopulations({ commit }) {
      commit('SET_REFERENCE_PATIENT_POPULATIONS_LOADED', false);
      return new Promise((resolve, reject) => {
        ReferenceService.getPatientPopulations()
          .then((response) => {
            // Contract #2: string[] (bare array), or { patientPopulations }.
            const patientPopulations = Array.isArray(response) ? response : response.patientPopulations || [];
            commit('SET_REFERENCE_PATIENT_POPULATIONS', patientPopulations);
            resolve();
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            commit('SET_REFERENCE_PATIENT_POPULATIONS_LOADED', true);
          });
      });
    },

    // Load every reference dataset once, when the user enters the Formulary
    // Management module. Self-guarded like `setMedicationModuleData`: if
    // everything is already loaded, resolves immediately so the router guard
    // can dispatch freely and components never need to fetch on mount.
    setReferenceModuleData({ dispatch, getters }) {
      if (getters.referenceModuleLoaded) {
        return Promise.resolve();
      }
      return Promise.all([dispatch('setReferenceFacilityNames'), dispatch('setReferencePatientPopulations')]);
    },
  },

  getters: {
    referenceFacilityNames: (state) => state.facilityNames,
    referenceFacilityNamesLoaded: (state) => state.facilityNamesLoaded,
    referencePatientPopulations: (state) => state.patientPopulations,
    referencePatientPopulationsLoaded: (state) => state.patientPopulationsLoaded,

    // True once every reference dataset has loaded.
    referenceModuleLoaded: (state) => state.facilityNamesLoaded && state.patientPopulationsLoaded,
  },
};
